import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../src/theme/getTheme';

export const getGameResultsPageStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 0,
    backgroundColor: theme.background.primary,
    ...(Platform.OS === 'web' && {
      height: '100vh' as never,
      overflow: 'hidden' as never,
    }),
  },
  /** Thin top bar shown when refetching results (placeholderData keeps previous data visible). */
  loadingBarTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: theme.primary,
    zIndex: 10,
  },
  /**
   * Web-only clearance for the absolutely-positioned PageHeader (≈60px overlay).
   * Native renders the header in-flow, so no spacer is needed there. This sits above
   * the GuestBanner so the banner is no longer painted behind the opaque header.
   */
  headerSpacer: {
    ...(Platform.OS === 'web' && { height: 60 as never }),
  },
  /** View mode switcher row (Date | League pill lives in AnimatedSegmentedControl) */
  viewModeSwitcherRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    // Web header clearance now lives in headerSpacer above the banner; keep a small gap here.
    paddingTop: Platform.OS === 'web' ? 8 : 14,
    paddingBottom: 2,
    paddingHorizontal: 16,
  },
  /** Slot for calendar (by date) or league picker (by league); same height so layout does not jump */
  pickerContainer: {
    paddingBottom: 6,
    width: '100%',
    minHeight: 100,
  },
  calendarMonthLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.white,
    textAlign: 'center',
    marginBottom: 2,
  },
  /** League mode: small gap between selector and games list */
  pickerContainerLeague: {
    paddingBottom: 4,
    minHeight: 0,
  },
  /** Hides section filter pills (All, Recent, Upcoming) while keeping them in the tree for state/logic */
  sectionFilterRowHidden: {
    height: 0,
    overflow: 'hidden',
    paddingTop: 0,
    paddingBottom: 0,
    margin: 0,
    opacity: 0,
  },
  sectionFilterChipsScroll: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  sectionFilterChipsScrollContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 12 : 10,
    paddingRight: 8,
  },
  /** Section filter pills: gamified, prominent */
  sectionFilterChip: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 22,
    backgroundColor: theme.gray[200],
    borderWidth: 1,
    borderColor: theme.gray[300],
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionFilterChipActive: {
    backgroundColor: theme.primary,
    borderColor: theme.primary,
  },
  sectionFilterChipPressed: {
    opacity: 0.8,
  },
  sectionFilterChipHover: {
    opacity: 0.9,
  },
  sectionFilterChipText: {
    fontSize: 13,
    fontWeight: '700',
    color: theme.gray[600],
  },
  sectionFilterChipTextActive: {
    fontSize: 13,
    fontWeight: '800',
    color: theme.white,
  },
  gamesList: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 8,
  },
  /** League mode: small top padding below league selector */
  gamesListLeague: {
    paddingTop: 8,
  },
});

